import {
  createContext,
  forwardRef,
  PropsWithChildren,
  useContext,
  useId,
} from 'react';
import { mergeProps, useRender, UseRenderComponentProps } from '@base-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleFadingArrowUp } from 'lucide-react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';

import {
  Button,
  FormField,
  Input,
  Notice,
  RadioGroup,
  ShimmerText,
  useDialog,
} from '@components/primitives';

import { AppObject, TeamObject } from '@api/types';
import { AppIcon } from '@components/features/Apps/AppIcon';
import { labelForPlan } from '@components/features/Settings/TeamBilling/utils';
import { UpgradeModal } from '@components/features/Upgrade/UpgradeCheckout/UpgradeModal';
import { subscribableLocalStorage } from '@hooks/use-local-state';
import { useAppsQuery, useCreateAppMutation } from '@queries/app';
import { useCurrentTeam } from '@queries/user';
import { monitoring } from '@services/monitoring';

import css from './styles.module.css';

import { Team } from '@/types';

function hasReachedProductionAppLimit(
  team: Team | TeamObject | undefined,
  apps: AppObject[] | undefined
) {
  if (!team) return false;
  if (team.tier !== 'free') return false;

  return apps?.some((app) => !app.sandbox) ?? false;
}

function getCreateAppSchema(
  team: Team | TeamObject | undefined,
  apps: AppObject[] | undefined
) {
  const appNames = apps?.map((app) => app.name.toUpperCase()) ?? [];

  return z.object({
    teamUuid: z.string(),

    name: z
      .string()
      .trim()
      .min(2, { message: 'App name must be at least 2 characters long' })
      .max(30, { message: "App name can't be longer than 30 characters" })
      .regex(/^[a-zA-Z0-9\s\-_']+$/i, {
        message:
          "App name can only contain alphanumeric characters, spaces, and '-_",
      })
      .refine((value) => !appNames.includes(value?.toUpperCase()), {
        message: 'An app with this name already exists',
      }),

    environment: z.enum(['sandbox', 'production']).refine(
      (value) => {
        if (value === 'production') {
          return !hasReachedProductionAppLimit(team, apps);
        }
        return true;
      },
      {
        message: 'You have reached the production app limit',
      }
    ),
  });
}
type CreateAppFormValues = z.infer<ReturnType<typeof getCreateAppSchema>>;

interface CreateAppFormContextValue {
  onCreate(app: AppObject): void;
  teamUuid: string;
  id: string;
  preventProductionApps: boolean;
}

const CreateAppFormContext = createContext<CreateAppFormContextValue>({
  onCreate() {},
  teamUuid: '',
  id: '',
  preventProductionApps: false,
});

export interface CreateAppFormProps extends PropsWithChildren {
  id?: string;
  teamUuid: string;
  defaultEnvironment?: CreateAppFormValues['environment'];
  onCreate(app: AppObject): void;
}

function Root({
  id: propId,
  teamUuid,
  defaultEnvironment = 'sandbox',
  onCreate,
  children,
}: CreateAppFormProps) {
  const internalId = useId();
  const id = propId ?? internalId;

  const { team } = useCurrentTeam();
  const { data: apps } = useAppsQuery(teamUuid);
  const preventProductionApps = hasReachedProductionAppLimit(team, apps);

  const schema = getCreateAppSchema(team, apps);
  const methods = useForm<CreateAppFormValues>({
    defaultValues: {
      teamUuid,
      name: '',
      environment: defaultEnvironment,
    },
    resolver: zodResolver(schema),
    mode: 'onTouched',
  });

  return (
    <CreateAppFormContext.Provider
      value={{ id, teamUuid, onCreate, preventProductionApps }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </CreateAppFormContext.Provider>
  );
}

function Fields() {
  const { id, onCreate, teamUuid, preventProductionApps } =
    useContext(CreateAppFormContext);
  const { team } = useCurrentTeam();

  const methods = useFormContext<CreateAppFormValues>();

  const upgradeDialog = useDialog();

  const { mutateAsync: createAppAsync } = useCreateAppMutation(teamUuid);
  const submit = methods.handleSubmit(async (values) => {
    await createAppAsync(
      {
        appName: values.name,
        sandbox: values.environment === 'sandbox',
      },
      {
        onError() {
          toast.error('Failed to create app');
        },
        onSuccess(response) {
          toast.success('App created');
          onCreate(response.app);
          monitoring.trackAction('app_created', {
            type: response.app.sandbox ? 'sandbox' : 'production',
          });
          try {
            subscribableLocalStorage.setItem(
              `app-created-tracked-${response.app.uuid}`,
              true
            );
          } catch {
            return;
          }
        },
      }
    );
  });

  return (
    <form id={id} onSubmit={submit}>
      <Controller
        control={methods.control}
        name="name"
        render={({ field, fieldState }) => (
          <FormField
            className="mb-4"
            label="App name"
            error={field.value ? fieldState.error?.message : undefined}
          >
            <Input
              {...field}
              placeholder="Staging"
              autoComplete="off"
              data-testid="team-form-name"
            />
          </FormField>
        )}
      />

      <FormField
        label="Environment"
        helperText="This setting cannot be changed later"
      >
        <Controller
          control={methods.control}
          name="environment"
          render={({ field }) => (
            <RadioGroup {...field} columns={1}>
              <RadioGroup.Item value="sandbox">
                <RadioGroup.Icon>
                  <AppIcon app={{ sandbox: true }} />
                </RadioGroup.Icon>
                <RadioGroup.Label>Sandbox</RadioGroup.Label>
                <RadioGroup.Description>
                  Test all Evervault features using simulated data
                </RadioGroup.Description>
              </RadioGroup.Item>

              <RadioGroup.Item
                value="production"
                disabled={preventProductionApps}
              >
                <RadioGroup.Icon>
                  <AppIcon app={{ sandbox: false }} />
                </RadioGroup.Icon>
                <RadioGroup.Label>Production</RadioGroup.Label>
                <RadioGroup.Description>
                  Used for live business operations and handling real user data
                </RadioGroup.Description>
              </RadioGroup.Item>
            </RadioGroup>
          )}
        />

        {preventProductionApps && (
          <>
            <Notice className={css.upgradeNotice} variant="warning">
              <Notice.Icon>
                <CircleFadingArrowUp />
              </Notice.Icon>
              <Notice.Title>{`You've reached the production app limit for your ${labelForPlan(team?.tier ?? 'free')} plan.`}</Notice.Title>
              <p>
                Need more apps?{' '}
                <Button
                  ghost
                  flush
                  type="button"
                  size="sm"
                  onClick={() => upgradeDialog.open()}
                >
                  <ShimmerText>Upgrade</ShimmerText>
                </Button>
              </p>
            </Notice>

            <UpgradeModal state={upgradeDialog} teamUuid={teamUuid} />
          </>
        )}
      </FormField>
    </form>
  );
}

interface SubmitProps extends UseRenderComponentProps<'button'> {}

const Submit = forwardRef<HTMLButtonElement, SubmitProps>(function Submit(
  { render, ...props },
  ref
) {
  const { id } = useContext(CreateAppFormContext);
  const methods = useFormContext<CreateAppFormValues>();
  const { isValid, isSubmitting } = methods.formState;

  return useRender({
    defaultTagName: 'button',
    ref,
    render,
    props: mergeProps(props, {
      form: id,
      type: 'submit',
      disabled: !isValid,
      loading: isSubmitting,
    }),
  });
});

export const CreateAppForm = Object.assign(Root, {
  Fields,
  Submit,
});
